import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import { SecondarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Pagination from 'components/ReusableComponents/Pagination'
import Table, {
  Cell,
  Row,
  TitleCell,
  TitleRow
} from 'components/ReusableComponents/Table'
import BookingStatusText from 'features/BookingDetails/BookingText'
import ViewMessageIcon from 'features/CustomCampaigns/CampaignResults/ViewMessageIcon'
import moment from 'moment'
import { useState } from 'react'
import config from 'services/config'
import { formatPrice } from 'utils/common'
import downloadAsCsv from 'utils/downloadAsCsv'

import { campaignNames } from '../SegmentsChart/constants'
import styles from './BookingListDialog.module.scss'
import { ReactComponent as ExcelIcon } from './excel.svg'

const BookingListDialog = ({
  currentSubsidiaryId,
  onClose,
  isLoading,
  isErred,
  category,
  stats,
  bookings,
  setActiveSubscriberId,
  setActiveBookingId,
  version,
  isPaginated,
  downloadCsv,
  downloadCSVLoading
}) => {
  const { pageIndex, pageSize, setPageIndex, totalSize } = isPaginated || {}

  const [sortIndex, setSortIndex] = useState(5)
  const [sortAscending, setSortAscending] = useState(true)
  const handlePageChange = newPage => {
    setPageIndex(newPage)
  }

  const totalPages = Math.ceil(totalSize / pageSize)

  const categoryName = campaignNames[category] || category
  const isRegainedLostGuests = version === 'RegainedLostGuests'
  const regainedLostGuestsTitle = `${'Guests booked after'} ${categoryName} ${'months'}`
  const conversionsBySegmentsTitle = categoryName
    ? `Conversions of ${categoryName}`
    : 'Conversions'

  const dialogTitle = isRegainedLostGuests
    ? regainedLostGuestsTitle
    : conversionsBySegmentsTitle

  function handleTitleClick(i) {
    const sortableIndexes = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]

    if (sortIndex === i) {
      setSortAscending(d => !d)
    }

    if (sortableIndexes.includes(i)) {
      setSortIndex(i)
    }
  }

  function handleClickDownload() {
    const titleRow = [
      'Guest name',
      'Days since last visit',
      'Miles to hotel',
      'Last visit date',
      'Number of bookings in last 12 months',
      'Invited on',
      'Days between invite and booking',
      'Booked on',
      'Days between booking and check-in',
      'Check-in date',
      'Check-out date',
      'Rate code',
      'Total rate',
      'Booking status',
      'Confirmation code'
    ]

    const resAsArrays = bookings.map(b => [
      b.fullName,
      b.bookedDaysAfterLastBooking,
      b.distanceToHotel ? Number(b.distanceToHotel).toFixed(2) : 0,
      b.lastStayDate === '0001-01-01T00:00:00'
        ? 'Cancelled'
        : moment(b.lastStayDate).format('YYYY/MM/DD'),
      b.numberOfBookingsLast12Month,
      moment(b.textedOn).format('YYYY/MM/DD'),
      b.bookingDaysAfterText,
      moment(b.bookingDate).format('YYYY/MM/DD'),
      b.bookDaysBeforeCheckIn,
      moment(b.checkIn).format('YYYY/MM/DD'),
      moment(b.checkOut).format('YYYY/MM/DD'),
      b.rateCode,
      b.totalRate ? Number(b.totalRate).toFixed(2) : 0,
      b.bookingStatus,
      b.confirmationCode
    ])

    const dataArray = [titleRow, ...resAsArrays]
    const fileName = `guests_booked_after_${category}_months.csv`

    downloadAsCsv(dataArray, fileName)
  }

  function sortResults(results) {
    const sortedData = results.sort((a, b) => {
      switch (sortIndex) {
        case 0:
          if (a.fullName === b.fullName) return 0

          return a?.fullName?.toUpperCase() > b?.fullName?.toUpperCase() ? 1 : -1

        case 1:
          return (
            Number(a.bookedDaysAfterLastBooking) - Number(b.bookedDaysAfterLastBooking)
          )

        case 2:
          return Number(a.distanceToHotel) - Number(b.distanceToHotel)

        case 3:
          return new Date(a.lastStayDate) - new Date(b.lastStayDate)

        case 4:
          return new Date(a.textedOn) - new Date(b.textedOn)

        case 5:
          return new Date(a.bookingDate) - new Date(b.bookingDate)

        case 6:
          return new Date(a.checkInDate) - new Date(b.checkInDate)

        case 7:
          return a.rateCode - b.rateCode

        case 8:
          return Number(a.totalRate) - Number(b.totalRate)

        case 9:
          return a.bookingStatus - b.bookingStatus

        default:
          return 0
      }
    })

    return sortAscending ? sortedData : [...sortedData].reverse()
  }

  function getDateRange(date1, date2) {
    if (moment(date1).format('MMM') === moment(date2).format('MMM')) {
      return `${moment(date1).format('MMM')} ${moment(date1).format('D')} - ${moment(
        date2
      ).format('D')}`
    } else {
      return `${moment(date1).format('MMM D')} - ${moment(date2).format('MMM D')}`
    }
  }

  const handleDownload = () => {
    if (version === 'RegainedLostGuests') {
      handleClickDownload()
    } else if (version === 'ConversionsBySegments') {
      downloadCsv()
    }
  }

  return (
    <ConfirmationDialog
      title={dialogTitle}
      maxWidth={1300}
      contentPadding={0}
      onClose={onClose}
      hasConfirmButton={false}
      allowOverflow={true}
    >
      {isErred && !isPaginated && <p>Something went wrong while loading results</p>}
      {isLoading && !isErred && !isPaginated && <LoadingIndicator inline />}
      {(isPaginated || (!isLoading && !isErred)) && (
        <>
          <div className={styles.bookingsWrapper}>
            <div className={styles.disclaimer}>
              This report includes all reclaimed guests regardless of booking method, rate
              code, etc. This report is not used for billing purposes.
            </div>
            <div className={styles.titleHolder}>
              {stats && (
                <h2 className={styles.bookingsTitle}>
                  {stats?.bookingsTotal || 0} bookings - Total rate{' '}
                  {stats?.bookingsTotalRate
                    ? formatPrice(stats.bookingsTotalRate)
                    : '$0.00'}
                </h2>
              )}

              <SecondaryRegularButton
                className={styles.copyToExcelButton}
                onClick={handleDownload}
              >
                {downloadCSVLoading ? (
                  <p>Downloading...</p>
                ) : (
                  <>
                    <ExcelIcon /> Download as CSV
                  </>
                )}
              </SecondaryRegularButton>
            </div>
            <div className={styles.tableHolder}>
              <Table
                widths={[140, 80, 70, 100, 100, 100, 100, 80, 80, 110, 60]}
                handleTitleCellClick={handleTitleClick}
                sortIndex={sortIndex}
                sortDirection={sortAscending ? 'up' : 'down'}
              >
                <TitleRow className={styles.titleRow}>
                  <TitleCell>Guest name</TitleCell>
                  <TitleCell>
                    Days since <br /> last stay
                  </TitleCell>
                  <TitleCell>
                    Miles <br /> to hotel
                  </TitleCell>
                  <TitleCell>Last stay</TitleCell>
                  <TitleCell>Texted on</TitleCell>
                  <TitleCell>Booked on</TitleCell>
                  <TitleCell>Stay dates</TitleCell>
                  <TitleCell>Rate code</TitleCell>
                  <TitleCell>Total rate</TitleCell>
                  <TitleCell>Booking status</TitleCell>
                  <TitleCell>Text</TitleCell>
                  <TitleCell></TitleCell>
                </TitleRow>
                {sortResults(bookings).map(b => (
                  <Row className={styles.row} key={b.bookingId}>
                    <Cell className={styles.fullNameRow}>
                      {b.subscriberId ? (
                        <button
                          className={styles.fullNameButton}
                          onClick={() => setActiveSubscriberId(b.subscriberId)}
                        >
                          {b.fullName}
                        </button>
                      ) : (
                        b.fullName
                      )}
                    </Cell>
                    <Cell>
                      {b.bookedDaysAfterLastBooking
                        ? b.bookedDaysAfterLastBooking
                        : 'No prev. booking'}
                    </Cell>
                    <Cell>{b.distanceToHotel || '-'}</Cell>
                    <Cell>
                      {b.lastStayDate === '0001-01-01T00:00:00'
                        ? 'Cancelled'
                        : moment(b.lastStayDate).format('MMM D, YYYY')}
                    </Cell>
                    <Cell>{moment(b.textedOn).format('MMM D, YYYY')}</Cell>
                    <Cell>{moment(b.bookingDate).format('MMM D, YYYY')}</Cell>
                    <Cell>{getDateRange(b.checkIn, b.checkOut)}</Cell>
                    <Cell className={styles.noOverflow}>
                      <span className={styles.cellNoOverflowInner} title={b.rateCode}>
                        {b.rateCode}
                      </span>
                    </Cell>
                    <Cell>{formatPrice(b.totalRate)}</Cell>
                    <Cell>
                      <BookingStatusText
                        status={b.bookingStatus}
                        bookingDate={b.bookingDate}
                      />
                    </Cell>
                    <Cell>
                      <div className={styles.previewHolder}>
                        <button className={styles.previewButton}>
                          <ViewMessageIcon />
                        </button>
                        <div className={styles.preview}>
                          {b.mediaUrl && (
                            <div
                              className={styles.textMedia}
                              style={{
                                backgroundImage: `url('${
                                  config.inviteMediaBaseUrl
                                }/${currentSubsidiaryId}/${encodeURIComponent(
                                  b.mediaUrl
                                )}')`
                              }}
                            />
                          )}
                          <div className={styles.bubble}>
                            <p>{b.text}</p>
                            <span className={styles.bubbleTail}></span>
                          </div>
                        </div>
                      </div>
                    </Cell>
                    <Cell>
                      <SecondarySmallButton
                        className={styles.viewButton}
                        onClick={() => setActiveBookingId(b.bookingId)}
                        disabled={!b.bookingId}
                      >
                        {' '}
                        Details
                      </SecondarySmallButton>
                    </Cell>
                  </Row>
                ))}
              </Table>
              {isLoading && isPaginated && (
                <div className={styles.loadingIndicatorContainer}>
                  {isErred && <p>Something went wrong while loading results</p>}
                  <LoadingIndicator inline />
                </div>
              )}
            </div>
            {isPaginated && (
              <Pagination
                totalPages={totalPages}
                activePageIndex={pageIndex}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </>
      )}
    </ConfirmationDialog>
  )
}

export default BookingListDialog
